import axios from 'axios';
import { getBytes, getStorage, ref } from 'firebase/storage';

import { webpack } from '@dr.pogodin/react-utils';
import { addBreadcrumb } from '@sentry/react';

import type * as ClientFirebaseServiceM from 'client/services/firebase';

import type { TextToSpeechQueryT, TextToSpeechResT } from 'server/controllers/openai';
import type { PostTranscribeResT } from 'server/routes/ai';
import type * as OpenAiNS from 'server/services/openai';

import {
  DEFAULT_VOICE,
  type VoiceT,
  calcCachedSpeechPath,
  getConfig,
} from 'utils';

import { getAudioClientService, getFirebaseClientService } from '../getClient';

export * from './getAiBuildMeta';
export * from './realtime-session';

// TODO: BEWARE: This seems to fail now, resulting in "null" `ss` value.
const ss = webpack.requireWeak<typeof OpenAiNS>('server/services/openai', import.meta.dirname);

async function getFirebase(): Promise<(typeof ClientFirebaseServiceM)['firebaseAppPromise']> {
  return (await getFirebaseClientService()).firebaseAppPromise;
}

const BASE = '/__api__/ai';

async function getFromFirebase(path: string): Promise<ArrayBuffer> {
  const file = ref(getStorage(await getFirebase()), path);
  return getBytes(file);
}

type SpeechOptionsT = {
  voice?: VoiceT;
};

export async function speech(
  text: string,
  { voice = DEFAULT_VOICE }: SpeechOptionsT = {},
): Promise<AudioBuffer> {
  const audio = await getAudioClientService();

  const path = await calcCachedSpeechPath(text, { voice });

  try {
    let data: ArrayBuffer;

    try {
      data = await getFromFirebase(path);
    } catch {
      // TODO: Technically, the URL it returns should match the same
      // file pointed by `path` (which is now created). So we may optimize
      // something here, with a few appropriate assertions added.
      const res = await axios<TextToSpeechResT>(`${BASE}/speech`, {
        params: { text, voice } satisfies TextToSpeechQueryT,
      });

      data = await getFromFirebase(res.data);
    }

    return await audio.getAudioContext().decodeAudioData(data);
  } catch (error) {
    // Synthesis/fetch/decode failed; callers don't catch, so log before rethrow.
    addBreadcrumb({
      category: 'tts-speech',
      data: { voice },
      level: 'error',
      message: 'TTS speech() failed',
    });
    throw error;
  }
}

/**
 * Transcribes provided audio file.
 */
export async function transcribe(
  audioBlob: Blob,
  duration: number, // [ms]
): Promise<string> {
  if (ss) {
    const buffer = Buffer.from(await audioBlob.arrayBuffer());
    return ss.transcribe(buffer, duration);
  }

  const config = await getConfig();

  const res = await axios.post<PostTranscribeResT>(`${BASE}/transcribe`, audioBlob, {
    headers: {
      'Content-Type': 'audio/webm',
      'audio-duration': duration,
      'csrf-token': config.CSRF,
    },
  });

  return res.data;
}
